import type {
  PermissionAction,
  PermissionGroup,
  PermissionKey,
  PermissionMap,
  PermissionModule,
  SystemRole,
} from '@/types/permission.types'
import { PERMISSION_MODULES } from '@/types/permission.types'

const MODULE_LABELS: Record<PermissionModule, string> = {
  dashboard: 'Dashboard',
  master_data: 'Overview',
  customers: 'Parties',
  suppliers: 'Vendors',
  sales_agents: 'Agents',
  categories: 'Categories',
  products: 'Designs',
  qualities: 'Qualities',
  colors: 'Colors',
  warehouses: 'Warehouses',
  delivery_challans: 'Dispatches',
  goods_received: 'Purchases',
  reports: 'Reports',
  purchase_reports: 'Purchase Summary',
  dispatch_reports: 'Dispatch Summary',
  sheets_sync: 'Sheets sync',
  users: 'Users',
  auth_settings: 'Security settings',
  backups: 'Backups',
  mail: 'Email',
  site_form: 'Website form',
  permissions: 'Permission Management',
  sales_leads: 'Queries',
  tasks: 'Tasks',
  messages: 'Messenger',
  quotations: 'Quotations',
  estimates: 'Estimates',
  bank_accounts: 'Bank accounts',
  catalog: 'Catalog',
  quotation_terms: 'Quotation Terms',
  calls: 'Calls',
  old_customers: 'Old customers',
}

/**
 * Actions exposed per built module.
 * Only include actions the UI actually gates today.
 */
const MODULE_ACTIONS: Record<PermissionModule, PermissionAction[]> = {
  dashboard: ['view', 'view_all', 'export', 'purchases'],
  master_data: ['view'],
  customers: ['view', 'create', 'update', 'delete', 'export'],
  suppliers: ['view', 'create', 'update', 'delete', 'export'],
  sales_agents: ['view', 'create', 'update', 'delete', 'link'],
  categories: ['view', 'create', 'update', 'delete'],
  products: ['view', 'create', 'update', 'delete'],
  qualities: ['view', 'create', 'update', 'delete'],
  colors: ['view', 'create', 'update', 'delete'],
  warehouses: ['view', 'create', 'update', 'delete'],
  delivery_challans: ['view', 'create', 'update', 'delete'],
  goods_received: ['view', 'create', 'update', 'delete'],
  reports: ['view', 'export'],
  purchase_reports: ['view', 'export'],
  dispatch_reports: ['view', 'export'],
  sheets_sync: ['run'],
  users: [
    'view',
    'create',
    'update',
    'delete',
    'manage_territory',
    'reset_password',
    'unlock',
    'manage_sessions',
    'view_auth_log',
    'reset_pin',
  ],
  auth_settings: ['manage'],
  backups: ['view', 'run'],
  mail: ['view', 'test', 'manage', 'team_report'],
  site_form: ['manage'],
  permissions: ['view', 'update'],
  sales_leads: [
    'view',
    'view_all',
    'create',
    'update',
    'assign',
    'own',
    'register',
    'remark',
    'delete',
    'summary',
    'summary_export',
    'inbox',
  ],
  tasks: ['view', 'view_all', 'create', 'assign', 'update', 'checklist', 'comment', 'delete'],
  messages: ['view', 'send', 'group_manage'],
  quotations: ['view', 'view_all', 'create', 'update', 'delete', 'select', 'export'],
  estimates: ['view', 'view_all', 'create', 'update', 'delete', 'export'],
  bank_accounts: ['view', 'create', 'update', 'delete'],
  catalog: ['view', 'create', 'update', 'delete'],
  quotation_terms: ['view', 'create', 'update', 'delete'],
  // More calls.* keys (history, dashboard, settings) appear with their screens.
  calls: ['dial'],
  old_customers: ['view', 'view_all', 'create', 'update', 'assign', 'convert', 'details', 'delete', 'give', 'export'],
}

export function buildPermissionKey(
  module: PermissionModule,
  action: PermissionAction,
): PermissionKey {
  return `${module}.${action}` as PermissionKey
}

export function getPermissionGroups(): PermissionGroup[] {
  return PERMISSION_MODULES.map((module) => ({
    module,
    label: MODULE_LABELS[module],
    actions: MODULE_ACTIONS[module],
  }))
}

/** Build a full permission map with every known key set to `enabled` */
export function buildAllPermissions(enabled: boolean): PermissionMap {
  const map: PermissionMap = {}
  for (const module of PERMISSION_MODULES) {
    for (const action of MODULE_ACTIONS[module]) {
      map[buildPermissionKey(module, action)] = enabled
    }
  }
  return map
}

export const ROLE_LABELS: Record<SystemRole, string> = {
  super_admin: 'Super Admin',
  manager: 'Manager',
  assistant: 'Assistant',
  salesman: 'Sales agent',
}

export const ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  super_admin: 'Full system access. Highest privilege level.',
  manager: 'Most operational permissions across modules.',
  assistant: 'Limited create and update on core operations.',
  salesman: 'Customer and sales related access only. Can own queries.',
}
